import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  Truck, Users, Wrench, MapPin, Route,
  AlertTriangle, Package, ShieldCheck, ShieldX,
  FileText, Snowflake, Thermometer, Server, Activity, RefreshCw
} from 'lucide-react';
import { getDashboard, getSparepartStats, getSystemHealth, getSystemHealthHistory } from '../api';

const COLORS = ['#11cdef', '#14b8c4', '#0d7a8a', '#2dce89', '#fb6340', '#f5365c', '#0a3845', '#073845', '#8965e0', '#06b6d4'];

// ─── Bento Stat Card ───
function BentoStat({ icon: Icon, label, value, sublabel, accentColor, iconBg, large }) {
  const CardTag = large ? 'div' : 'div';
  return (
    <CardTag className={`bento-stat ${large ? 'bento-stat-lg' : ''}`}>
      <div className="bento-stat-accent" style={{ background: `linear-gradient(90deg, ${accentColor}, ${accentColor}66)` }} />
      <div className="bento-stat-body">
        <div className="bento-stat-content">
          <span className="bento-stat-label">{label}</span>
          <span className="bento-stat-value">{value?.toLocaleString() || 0}</span>
          {sublabel && <span className="bento-stat-sublabel">{sublabel}</span>}
        </div>
        <div className="bento-stat-icon" style={{ background: iconBg || accentColor + '18', color: accentColor }}>
          <Icon size={large ? 28 : 22} />
        </div>
      </div>
    </CardTag>
  );
}

// ─── Chart Card (modern) ───
function ChartCard({ title, subtitle, children, className = '' }) {
  return (
    <div className={`chart-card glass-card ${className}`}>
      <div className="chart-card-header">
        <h6 className="chart-card-title">{title}</h6>
        {subtitle && <span className="chart-card-subtitle">{subtitle}</span>}
      </div>
      <div className="chart-card-body">
        {children}
      </div>
    </div>
  );
}

// ─── Custom Tooltip ───
function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="tooltip-item" style={{ color: p.color }}>
            {p.name}: <strong>{p.value?.toLocaleString()}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
}

// ─── Health Bar (CPU/RAM/ROM) ───
function healthColor(pct) {
  if (pct >= 85) return '#f5365c';
  if (pct >= 65) return '#fb6340';
  return '#2dce89';
}

function HealthBar({ icon: Icon, label, percent, detail, unit = '%' }) {
  const pct = Math.min(Math.max(percent || 0, 0), 100);
  const color = healthColor(pct);
  return (
    <div className="health-bar">
      <div className="health-bar-top">
        <span className="health-bar-label">
          <Icon size={15} style={{ color }} />
          <span>{label}</span>
        </span>
        <span className="health-bar-value" style={{ color }}>
          <strong>{pct.toFixed(1)}{unit}</strong>
          <span className="health-bar-detail">{detail}</span>
        </span>
      </div>
      <div className="health-bar-track">
        <div className="health-bar-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}88, ${color})` }} />
      </div>
    </div>
  );
}

// ─── Progress Bar List ───
function ProgressBarList({ data, colorKey = 'name', valueKey = 'count', colors }) {
  const maxVal = Math.max(...data.map(d => d[valueKey]), 1);
  return (
    <div className="region-bar-list">
      {data.map((d, i) => {
        const pct = (d[valueKey] / maxVal) * 100;
        const c = colors[i % colors.length];
        return (
          <div key={i} className="region-bar-item" style={{ transition: 'transform 0.2s', cursor: 'default' }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateX(4px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateX(0)'}>
            <div className="region-bar-label">
              <span className="region-dot" style={{ background: c }} />
              <span className="region-name">{d[colorKey]}</span>
            </div>
            <div className="region-bar-track">
              <div className="region-bar-fill"
                style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${c}, ${c}88)` }} />
            </div>
            <span className="region-bar-count">{d[valueKey]}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Doc Status Badge ───
function DocBadge({ label, valid, expired }) {
  const total = (valid || 0) + (expired || 0);
  const pct = total > 0 ? Math.round((valid / total) * 100) : 0;
  return (
    <div className="doc-status-item">
      <div className="doc-status-header">
        <span className="doc-status-label">{label}</span>
        <span className="doc-status-pct">{pct}% Valid</span>
      </div>
      <div className="doc-status-bars">
        <div className="doc-status-bar">
          <div className="doc-status-fill doc-status-valid"
            style={{ width: `${pct}%` }} />
        </div>
        <div className="doc-status-bar">
          <div className="doc-status-fill doc-status-expired"
            style={{ width: `${100 - pct}%` }} />
        </div>
      </div>
      <div className="doc-status-footer">
        <span className="doc-status-valid-count">
          <ShieldCheck size={12} /> {valid || 0} Valid
        </span>
        <span className="doc-status-expired-count">
          <ShieldX size={12} /> {expired || 0} Expired
        </span>
      </div>
    </div>
  );
}

// ─── Page Header ───
function PageHeader() {
  return (
    <div className="dash-header">
      <div className="dash-header-bg" />
      <div className="dash-header-content">
        <div className="dash-breadcrumb">
          <span>Halaman Utama</span>
          <span className="dash-breadcrumb-sep">/</span>
          <span className="dash-breadcrumb-current">Dashboard Armada</span>
        </div>
        <h2 className="dash-title">Dashboard Armada</h2>
        <p className="dash-subtitle">Overview status dan performa kendaraan KWK</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [spStats, setSpStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthHistory, setHealthHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load data dashboard + kesehatan server
  useEffect(() => {
    Promise.all([getDashboard(), getSparepartStats()])
      .then(([dashRes, spRes]) => {
        setStats(dashRes.data);
        setSpStats(spRes.data);
      })
      .catch(err => {
        console.error('Failed to load dashboard', err);
        setError('Gagal memuat data dashboard. Periksa koneksi server.');
      })
      .finally(() => setLoading(false));
  }, []);

  // Kesehatan server: muat awal + auto-refresh tiap 60 detik
  useEffect(() => {
    let active = true;
    const loadHealth = () => {
      getSystemHealth()
        .then(res => { if (active) setHealth(res.data); })
        .catch(err => console.error('Failed to load system health', err))
        .finally(() => { if (active) setHealthLoading(false); });
      // Riwayat 24 jam — muat ulang tiap 5 menit (data baru tiap 5 mnt)
      getSystemHealthHistory(24)
        .then(res => { if (active && res.data?.history) setHealthHistory(res.data.history); })
        .catch(err => console.error('Failed to load system health history', err));
    };
    loadHealth();
    const timer = setInterval(loadHealth, 60000);
    const histTimer = setInterval(() => getSystemHealthHistory(24)
      .then(res => { if (active && res.data?.history) setHealthHistory(res.data.history); })
      .catch(() => {}), 300000);
    return () => { active = false; clearInterval(timer); clearInterval(histTimer); };
  }, []);

  // ── Derived Data ──
  const clientData = stats?.by_client?.slice(0, 8) || [];
  const teknisiData = stats?.by_teknisi || [];
  const routeData = stats?.by_route?.slice(0, 12) || [];
  const pengendapanData = stats?.by_pengendapan?.slice(0, 12) || [];
  const regionData = stats?.vehicles_by_region?.slice(0, 12) || [];
  const brandData = stats?.by_brand?.slice(0, 10) || [];
  const contractData = stats?.contract_types || [];
  const docStatus = stats?.doc_status || {};
  const acTotal = (stats?.ac_count || 0) + (stats?.non_ac_count || 0);
  const acPct = acTotal > 0 ? Math.round(((stats?.ac_count || 0) / acTotal) * 100) : 0;

  const totalDocs = (docStatus.stnk_valid || 0) + (docStatus.stnk_expired || 0) +
    (docStatus.kir_valid || 0) + (docStatus.kir_expired || 0) +
    (docStatus.kp_valid || 0) + (docStatus.kp_expired || 0);

  const acData = [
    { name: 'AC', value: stats?.ac_count || 0, color: '#0a66c2' },
    { name: 'Non AC', value: stats?.non_ac_count || 0, color: '#d1d5db' },
  ];

  // ── Loading State ──
  if (loading) {
    return (
      <div className="dash-wrapper">
        <PageHeader />
        <div className="dash-content">
          <div className="dash-loading">
            <div className="spinner-lg" />
            <p>Memuat dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Error State ──
  if (error || !stats) {
    return (
      <div className="dash-wrapper">
        <PageHeader />
        <div className="dash-content">
          <div className="dash-error">
            <AlertTriangle size={32} />
            <p>{error || 'Tidak dapat memuat data. Pastikan server backend berjalan.'}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Vehicle Type ──
  const typeData = stats.vehicle_type_stats?.slice(0, 6) || [];

  return (
    <div className="dash-wrapper">
      <PageHeader />
      <div className="dash-content">

        {/* ═══════ ROW 1: BENTO STATS ═══════ */}
        <div className="bento-row">
          <BentoStat
            icon={Truck}
            label="Total Kendaraan"
            value={stats.total_vehicles}
            sublabel="Terdaftar dalam sistem"
            accentColor="#11cdef"
            iconBg="rgba(17,205,239,0.12)"
            large
          />
          <BentoStat
            icon={Users}
            label="Total Klien"
            value={stats.client_count}
            sublabel={`${clientData[0]?.name || ''}`}
            accentColor="#2dce89"
            iconBg="rgba(45,206,137,0.12)"
          />
          <BentoStat
            icon={Wrench}
            label="Teknisi"
            value={stats.teknisi_count}
            sublabel={`${teknisiData[0]?.name || ''} & ${teknisiData.length - 1} lainnya`}
            accentColor="#fb6340"
            iconBg="rgba(251,99,64,0.12)"
          />
          <BentoStat
            icon={Route}
            label="Rute"
            value={stats?.by_route?.length || 0}
            sublabel="Jalur operasional"
            accentColor="#f5365c"
            iconBg="rgba(245,54,92,0.12)"
          />
          <BentoStat
            icon={MapPin}
            label="Pengendapan"
            value={stats?.by_pengendapan?.length || 0}
            sublabel="Lokasi pool/garasi"
            accentColor="#14b8c4"
            iconBg="rgba(20,184,196,0.12)"
          />
        </div>

        <div className="dash-charts-row">
          <div className="dash-chart-col-3">
            <BentoStat
              icon={Snowflake}
              label="Armada AC"
              value={stats.ac_count}
              sublabel={`${acPct}% dari total`}
              accentColor="#0a66c2"
              iconBg="rgba(10,102,194,0.12)"
            />
          </div>
          <div className="dash-chart-col-3">
            <BentoStat
              icon={Thermometer}
              label="Armada Non AC"
              value={stats.non_ac_count}
              sublabel={`${100 - acPct}% dari total`}
              accentColor="#8898aa"
              iconBg="rgba(136,152,170,0.12)"
            />
          </div>
          <div className="dash-chart-col-3">
            <BentoStat
              icon={Users}
              label="Klien Terbesar"
              value={clientData[0]?.count || 0}
              sublabel={clientData[0]?.name || '-'}
              accentColor="#0d7a8a"
              iconBg="rgba(13,122,138,0.12)"
            />
          </div>
          <div className="dash-chart-col-3">
            <BentoStat
              icon={Wrench}
              label="Teknisi Terbanyak"
              value={teknisiData[0]?.count || 0}
              sublabel={teknisiData[0]?.name || '-'}
              accentColor="#f5365c"
              iconBg="rgba(245,54,92,0.12)"
            />
          </div>
        </div>

        {/* ═══════ ROW 2: CHARTS ═══════ */}
        <div className="dash-charts-row">
          {/* AC Donut */}
          <div className="dash-chart-col-4">
            <ChartCard title="Status AC" subtitle={`${stats.ac_count} AC · ${stats.non_ac_count} Non AC`}>
              <div className="donut-wrapper">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <defs>
                      <filter id="donutShadow">
                        <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.12" />
                      </filter>
                    </defs>
                    <Pie
                      data={acData}
                      cx="50%" cy="50%"
                      innerRadius={70} outerRadius={100}
                      paddingAngle={3}
                      dataKey="value"
                      filter="url(#donutShadow)"
                    >
                      {acData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-glow">
                  <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0a66c2', lineHeight: 1 }}>
                    {acPct}%
                  </span>
                  <span style={{ fontSize: '0.65rem', color: '#8898aa', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    AC
                  </span>
                </div>
              </div>
              <div className="donut-legend" style={{ marginTop: 12 }}>
                <div className="donut-legend-item">
                  <span className="donut-dot" style={{ background: '#0a66c2' }} />
                  <span>AC: {stats.ac_count}</span>
                </div>
                <div className="donut-legend-item">
                  <span className="donut-dot" style={{ background: '#d1d5db' }} />
                  <span>Non AC: {stats.non_ac_count}</span>
                </div>
              </div>
            </ChartCard>
          </div>

          {/* Per Teknisi Bar */}
          <div className="dash-chart-col-4">
            <ChartCard title="Per Teknisi" subtitle={`${stats.teknisi_count} teknisi`}>
              {teknisiData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={teknisiData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f5" vertical={false} />
                    <XAxis dataKey="name" stroke="#adb5bd" fontSize={12} />
                    <YAxis stroke="#adb5bd" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <defs>
                      {teknisiData.map((_, idx) => (
                        <linearGradient key={idx} id={`teknisiGrad${idx}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={1} />
                          <stop offset="100%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={0.45} />
                        </linearGradient>
                      ))}
                    </defs>
                    <Bar dataKey="count" radius={[8, 8, 0, 0]} barSize={42}>
                      {teknisiData.map((_, idx) => (
                        <Cell key={idx} fill={`url(#teknisiGrad${idx})`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted text-sm">Tidak ada data teknisi.</p>
              )}
            </ChartCard>
          </div>

          {/* Per Klien Horizontal Bar */}
          <div className="dash-chart-col-4">
            <ChartCard title="Per Klien" subtitle={`${stats.client_count} klien`}>
              {clientData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={clientData}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 110, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f5" horizontal={false} />
                    <XAxis type="number" stroke="#adb5bd" fontSize={11} />
                    <YAxis type="category" dataKey="name" stroke="#adb5bd" fontSize={8} width={105} tickFormatter={v => v.length > 18 ? v.substring(0, 16) + '\u2026' : v} />
                    <Tooltip content={<CustomTooltip />} />
                    <defs>
                      {clientData.map((_, idx) => (
                        <linearGradient key={idx} id={`clientGrad${idx}`} x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={0.85} />
                          <stop offset="100%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={0.35} />
                        </linearGradient>
                      ))}
                    </defs>
                    <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={20}>
                      {clientData.map((_, idx) => (
                        <Cell key={idx} fill={`url(#clientGrad${idx})`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted text-sm">Tidak ada data klien.</p>
              )}
            </ChartCard>
          </div>
        </div>

        {/* ═══════ ROW 3: DETAIL DISTRIBUSI ═══════ */}
        <div className="dash-charts-row">
          {/* Klien Detail */}
          <div className="dash-chart-col-4">
            <ChartCard title="Distribusi Per Klien" subtitle={`${stats.client_count} klien · ${stats.total_vehicles} kendaraan`}>
              {clientData.length > 0 ? (
                <ProgressBarList data={clientData} colors={COLORS} />
              ) : (
                <p className="text-muted text-sm">Tidak ada data klien.</p>
              )}
            </ChartCard>
          </div>

          {/* Wilayah/Region Distribution - NEW */}
          <div className="dash-chart-col-4">
            <ChartCard title="Distribusi Wilayah" subtitle={`${regionData.length} wilayah`}>
              {regionData.length > 0 ? (
                <ProgressBarList data={regionData} colorKey="region" colors={[...COLORS].reverse()} />
              ) : (
                <p className="text-muted text-sm">Tidak ada data wilayah.</p>
              )}
            </ChartCard>
          </div>

          {/* Pengendapan Detail */}
          <div className="dash-chart-col-4">
            <ChartCard title="Distribusi Pengendapan" subtitle={`${pengendapanData.length} lokasi pool/garasi`}>
              {pengendapanData.length > 0 ? (
                <ProgressBarList data={pengendapanData} colors={[...COLORS].reverse()} />
              ) : (
                <p className="text-muted text-sm">Tidak ada data pengendapan.</p>
              )}
            </ChartCard>
          </div>
        </div>

        {/* ═══════ ROW 4: ROUTE + BRAND + CONTRACT ═══════ */}
        <div className="dash-charts-row">
          {/* Route Distribution */}
          <div className="dash-chart-col-4">
            <ChartCard title="Distribusi Route" subtitle={`${routeData.length} rute teratas`}>
              {routeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.min(320, 40 + routeData.length * 22)}>
                  <BarChart data={routeData} layout="vertical" margin={{ top: 5, right: 30, left: 70, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f5" horizontal={false} />
                    <XAxis type="number" stroke="#adb5bd" fontSize={11} />
                    <YAxis type="category" dataKey="name" stroke="#adb5bd" fontSize={9} width={65} />
                    <Tooltip content={<CustomTooltip />} />
                    <defs>
                      <linearGradient id="routeGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#11cdef" stopOpacity={0.85} />
                        <stop offset="100%" stopColor="#0d7a8a" stopOpacity={0.4} />
                      </linearGradient>
                    </defs>
                    <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={16} fill="url(#routeGrad)" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted text-sm">Tidak ada data route.</p>
              )}
            </ChartCard>
          </div>

          {/* Brand Distribution - NEW */}
          <div className="dash-chart-col-4">
            <ChartCard title="Distribusi Merek" subtitle={`${brandData.length} merek kendaraan`}>
              {brandData.length > 0 ? (
                <ProgressBarList data={brandData} colorKey="brand" colors={COLORS} />
              ) : (
                <p className="text-muted text-sm">Tidak ada data merek.</p>
              )}
            </ChartCard>
          </div>

          {/* Contract Types */}
          <div className="dash-chart-col-4">
            <ChartCard title="No Kontrak" subtitle={`${contractData.length} jenis`}>
              <div className="contract-stats">
                {contractData.map((ct, i) => {
                  const maxCt = Math.max(...contractData.map(c => c.count), 1);
                  const pct = (ct.count / maxCt) * 100;
                  return (
                    <div key={i} className="contract-item">
                      <div className="contract-header">
                        <span className="contract-name">{ct.category === '0' ? 'Belum diisi' : ct.category}</span>
                        <span className="contract-count">{ct.count}</span>
                      </div>
                      <div className="contract-bar-track">
                        <div className="contract-bar-fill"
                          style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${COLORS[i % COLORS.length]}, ${COLORS[(i + 1) % COLORS.length]})` }} />
                      </div>
                    </div>
                  );
                })}
                {contractData.length === 0 && (
                  <p className="text-muted text-sm">Tidak ada data kontrak.</p>
                )}
              </div>
            </ChartCard>
          </div>
        </div>

        {/* ═══════ ROW 5: DOC STATUS + QUICK FACTS + VEHICLE TYPES ═══════ */}
        <div className="dash-charts-row">
          {/* Document Status - NEW */}
          <div className="dash-chart-col-4">
            <ChartCard title="Status Dokumen" subtitle={`${totalDocs > 0 ? `${docStatus.stnk_valid + docStatus.kir_valid + docStatus.kp_valid} Valid / ${docStatus.stnk_expired + docStatus.kir_expired + docStatus.kp_expired} Expired` : 'Belum ada data'}`}>
              {totalDocs > 0 ? (
                <div className="doc-status-list">
                  <DocBadge label="STNK" valid={docStatus.stnk_valid} expired={docStatus.stnk_expired} />
                  <DocBadge label="KIR" valid={docStatus.kir_valid} expired={docStatus.kir_expired} />
                  <DocBadge label="KP" valid={docStatus.kp_valid} expired={docStatus.kp_expired} />
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <FileText size={36} color="#8898aa" style={{ marginBottom: 8 }} />
                  <p className="text-muted">Data dokumen belum tersedia</p>
                </div>
              )}
            </ChartCard>
          </div>

          {/* Quick Facts */}
          <div className="dash-chart-col-4">
            <ChartCard title="Ringkasan Armada" subtitle="Fakta cepat kendaraan">
              <div className="bento-quick-facts">
                <div className="bento-quick-fact">
                  <span className="bento-qf-label">
                    <span className="bento-qf-dot" style={{ background: '#11cdef' }} />
                    Total Kendaraan
                  </span>
                  <span className="bento-qf-value">{stats.total_vehicles}</span>
                </div>
                <div className="bento-quick-fact">
                  <span className="bento-qf-label">
                    <span className="bento-qf-dot" style={{ background: '#2dce89' }} />
                    Kendaraan Aktif
                  </span>
                  <span className="bento-qf-value">{stats.active_vehicles}</span>
                </div>
                <div className="bento-quick-fact">
                  <span className="bento-qf-label">
                    <span className="bento-qf-dot" style={{ background: '#fb6340' }} />
                    Armada AC
                  </span>
                  <span className="bento-qf-value">{stats.ac_count}</span>
                </div>
                <div className="bento-quick-fact">
                  <span className="bento-qf-label">
                    <span className="bento-qf-dot" style={{ background: '#8898aa' }} />
                    Non AC
                  </span>
                  <span className="bento-qf-value">{stats.non_ac_count}</span>
                </div>
                <div className="bento-quick-fact">
                  <span className="bento-qf-label">
                    <span className="bento-qf-dot" style={{ background: '#f5365c' }} />
                    Jumlah Klien
                  </span>
                  <span className="bento-qf-value">{stats.client_count}</span>
                </div>
                <div className="bento-quick-fact">
                  <span className="bento-qf-label">
                    <span className="bento-qf-dot" style={{ background: '#14b8c4' }} />
                    Jumlah Teknisi
                  </span>
                  <span className="bento-qf-value">{stats.teknisi_count}</span>
                </div>
                <div className="bento-quick-fact">
                  <span className="bento-qf-label">
                    <span className="bento-qf-dot" style={{ background: '#0d7a8a' }} />
                    Rute Operasional
                  </span>
                  <span className="bento-qf-value">{stats?.by_route?.length || 0}</span>
                </div>
                <div className="bento-quick-fact">
                  <span className="bento-qf-label">
                    <span className="bento-qf-dot" style={{ background: '#0a66c2' }} />
                    Wilayah
                  </span>
                  <span className="bento-qf-value">{regionData.length}</span>
                </div>
                <div className="bento-quick-fact">
                  <span className="bento-qf-label">
                    <span className="bento-qf-dot" style={{ background: '#8965e0' }} />
                    Merek Tersedia
                  </span>
                  <span className="bento-qf-value">{brandData.length}</span>
                </div>
                <div className="bento-quick-fact">
                  <span className="bento-qf-label">
                    <span className="bento-qf-dot" style={{ background: '#06b6d4' }} />
                    Lokasi Pool
                  </span>
                  <span className="bento-qf-value">{stats?.by_pengendapan?.length || 0}</span>
                </div>
              </div>
            </ChartCard>
          </div>

          {/* Vehicle Types */}
          <div className="dash-chart-col-4">
            <ChartCard title="Tipe Kendaraan" subtitle={`${typeData.length} tipe armada`}>
              {typeData.length > 0 ? (
                <div className="type-grid">
                  {typeData.map((t, i) => (
                    <div key={i} className="type-chip" style={{ transition: 'all 0.2s', cursor: 'default' }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                      <span className="type-dot" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="type-name">{t.type}</span>
                      <span className="type-count">{t.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted text-sm">Tidak ada data tipe kendaraan.</p>
              )}
            </ChartCard>
          </div>
        </div>

        {/* ═══════ SERVER HEALTH SECTION ═══════ */}
        <div className="dash-charts-row">
          <div className="dash-chart-col-12">
            <ChartCard
              title="Kesehatan Server VPS"
              subtitle={health ? `${health.hostname || 'Server'} · up ${health.uptime || '-'} · diperbarui ${health.timestamp || ''}` : 'Memuat data server...'}
            >
              {healthLoading && !health ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                  <div className="spinner-sm" style={{ borderColor: '#11cdef33', borderTopColor: '#11cdef' }} />
                </div>
              ) : health ? (
                <>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    {/* CPU */}
                    <div style={{ flex: '1 1 240px', minWidth: 220 }}>
                      <HealthBar
                        icon={Activity}
                        label="CPU"
                        percent={health.cpu_percent}
                        detail={`${health.cpu_cores || '-'} core · Go ${health.go_version || ''}`}
                      />
                    </div>
                    {/* RAM */}
                    <div style={{ flex: '1 1 240px', minWidth: 220 }}>
                      <HealthBar
                        icon={Server}
                        label="RAM"
                        percent={health.ram?.percent}
                        detail={`${health.ram?.used_gb ?? 0} / ${health.ram?.total_gb ?? 0} GB`}
                      />
                    </div>
                    {/* ROM / Disk */}
                    <div style={{ flex: '1 1 240px', minWidth: 220 }}>
                      <HealthBar
                        icon={Package}
                        label="ROM / Disk"
                        percent={health.disk?.percent}
                        detail={`${health.disk?.used_gb ?? 0} / ${health.disk?.total_gb ?? 0} GB`}
                      />
                    </div>
                  </div>

                  {/* Grafik riwayat 24 jam */}
                  {healthHistory.length > 0 && (
                    <div style={{ marginTop: 22, borderTop: '1px solid #eef2f7', paddingTop: 16 }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#525f7f' }}>
                        Riwayat 24 Jam (CPU / RAM / Disk)
                      </span>
                      <ResponsiveContainer width="100%" height={220} style={{ marginTop: 10 }}>
                        <LineChart data={healthHistory} margin={{ top: 14, right: 10, left: -18, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f5" vertical={false} />
                          <XAxis
                            dataKey="time"
                            stroke="#adb5bd"
                            fontSize={10}
                            tickFormatter={v => {
                              const d = new Date(v);
                              return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                            }}
                            minTickGap={40}
                          />
                          <YAxis domain={[0, 100]} unit="%" stroke="#adb5bd" fontSize={10} />
                          <Tooltip
                            formatter={(value, name) => [`${value}%`, name === 'ram' ? 'RAM' : name === 'disk' ? 'Disk' : 'CPU']}
                            labelFormatter={v => {
                              const d = new Date(v);
                              return d.toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Line type="monotone" dataKey="cpu" stroke="#11cdef" strokeWidth={2} dot={false} name="CPU" />
                          <Line type="monotone" dataKey="ram" stroke="#2dce89" strokeWidth={2} dot={false} name="RAM" />
                          <Line type="monotone" dataKey="disk" stroke="#fb6340" strokeWidth={2} dot={false} name="Disk" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* User aktif */}
                  <div className="health-users" style={{ marginTop: 20, borderTop: '1px solid #eef2f7', paddingTop: 16 }}>
                    <div className="health-users-header">
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#525f7f' }}>
                        <Users size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
                        User Sedang Akses
                      </span>
                      <button
                        className="health-refresh-btn"
                        onClick={() => {
                          setHealthLoading(true);
                          getSystemHealth()
                            .then(res => setHealth(res.data))
                            .catch(() => {})
                            .finally(() => setHealthLoading(false));
                        }}
                        title="Segarkan data server"
                        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid #e3e8ef', borderRadius: 8, padding: '4px 10px', fontSize: '0.72rem', color: '#525f7f', cursor: 'pointer' }}
                      >
                        <RefreshCw size={13} className={healthLoading ? 'spin' : ''} />
                        Segarkan
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 12 }}>
                      <div className="health-user-count">
                        <span className="health-user-num" style={{ color: '#2dce89' }}>{health.active_users?.web ?? 0}</span>
                        <span className="health-user-lbl">Web / Admin aktif (15 mnt)</span>
                      </div>
                      <div className="health-user-count">
                        <span className="health-user-num" style={{ color: '#11cdef' }}>{health.active_users?.mobile ?? 0}</span>
                        <span className="health-user-lbl">Sesi Mobile aktif</span>
                      </div>
                    </div>
                    {health.active_users?.list?.length > 0 && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                        {health.active_users.list.map((u, i) => (
                          <span key={i} className="health-user-chip">
                            {u.username} <small>{u.requests} req · {u.last_seen}</small>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Detail sesi mobile: perangkat + IP */}
                    {health.active_users?.mobile_sessions?.length > 0 && (
                      <div style={{ marginTop: 14, overflowX: 'auto' }}>
                        <table className="health-session-table">
                          <thead>
                            <tr>
                              <th>User</th>
                              <th>Perangkat</th>
                              <th>IP Address</th>
                              <th>Login</th>
                              <th>Berlaku s/d</th>
                            </tr>
                          </thead>
                          <tbody>
                            {health.active_users.mobile_sessions.map((s, i) => (
                              <tr key={s.token_id || i}>
                                <td><strong>{s.username || '-'}</strong></td>
                                <td>
                                  <span className="health-device-chip" title={s.ua || ''}>{s.device || '-'}</span>
                                </td>
                                <td className="health-ip">{s.ip || '-'}</td>
                                <td className="health-time">{s.created_at || '-'}</td>
                                <td className="health-time">{s.expires_at || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-muted text-sm">Tidak dapat memuat data server.</p>
              )}
            </ChartCard>
          </div>
        </div>

        {/* ═══════ SPAREPART SECTION ═══════ */}
        {spStats && (
          <>
            <div className="dash-charts-row">
              <div className="dash-chart-col-12">
                <ChartCard title="Ringkasan Sparepart" subtitle="Inventaris suku cadang">
                  <div className="insurance-stats">
                    <div className="insurance-stat-item">
                      <Package size={20} color="#11cdef" />
                      <div>
                        <span className="insurance-stat-value">{spStats.total_spareparts}</span>
                        <span className="insurance-stat-label">Total Item</span>
                      </div>
                    </div>
                    <div className="insurance-stat-item">
                      <Package size={20} color="#2dce89" />
                      <div>
                        <span className="insurance-stat-value">{spStats.total_stock?.toLocaleString()}</span>
                        <span className="insurance-stat-label">Total Stok</span>
                      </div>
                    </div>
                    <div className="insurance-stat-item">
                      <Package size={20} color="#fb6340" />
                      <div>
                        <span className="insurance-stat-value">
                          Rp {(spStats.total_value || 0).toLocaleString('id-ID', { maximumFractionDigits: 0 })}
                        </span>
                        <span className="insurance-stat-label">Nilai Inventaris</span>
                      </div>
                    </div>
                    <div className="insurance-stat-item">
                      <AlertTriangle size={20} color={spStats.low_stock_items > 0 ? '#f5365c' : '#2dce89'} />
                      <div>
                        <span className="insurance-stat-value">{spStats.low_stock_items}</span>
                        <span className="insurance-stat-label">Stok Rendah ({'<='}5)</span>
                      </div>
                    </div>
                  </div>
                </ChartCard>
              </div>
            </div>

            <div className="dash-charts-row">
              <div className="dash-chart-col-6">
                <ChartCard title="Sparepart Termahal" subtitle="Top 10 berdasarkan harga">
                  {spStats.top_price?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={spStats.top_price} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f5" horizontal={false} />
                        <XAxis type="number" stroke="#adb5bd" fontSize={11} tickFormatter={v => `Rp${(v / 1000).toFixed(0)}rb`} />
                        <YAxis type="category" dataKey="name" stroke="#adb5bd" fontSize={10} width={75} />
                        <Tooltip content={({ active, payload }) => {
                          if (active && payload?.length) {
                            const d = payload[0].payload;
                            return (
                              <div className="custom-tooltip">
                                <p className="tooltip-label">{d.name}</p>
                                <p style={{ color: '#11cdef', fontSize: 13 }}>Rp {d.price?.toLocaleString('id-ID')}</p>
                                <p style={{ color: '#8898aa', fontSize: 12 }}>Stok: {d.stock} {d.unit}</p>
                              </div>
                            );
                          }
                          return null;
                        }} />
                        <defs>
                          <linearGradient id="topPriceGrad" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#11cdef" stopOpacity={0.85} />
                            <stop offset="100%" stopColor="#0d7a8a" stopOpacity={0.35} />
                          </linearGradient>
                        </defs>
                        <Bar dataKey="price" radius={[0, 6, 6, 0]} barSize={14} fill="url(#topPriceGrad)" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-muted text-sm">Tidak ada data sparepart.</p>
                  )}
                </ChartCard>
              </div>

              <div className="dash-chart-col-3">
                <ChartCard title="Status Stok">
                  {spStats.stock_status?.length > 0 ? (
                    <div className="donut-wrapper">
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie data={spStats.stock_status} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="count">
                            {spStats.stock_status.map((entry, idx) => {
                              const colors = ['#f5365c', '#fb6340', '#2dce89'];
                              return <Cell key={idx} fill={colors[idx % colors.length]} />;
                            })}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="donut-legend" style={{ flexDirection: 'column', alignItems: 'center' }}>
                        {spStats.stock_status.map((s, i) => {
                          const colors = ['#f5365c', '#fb6340', '#2dce89'];
                          return (
                            <div key={i} className="donut-legend-item">
                              <span className="donut-dot" style={{ background: colors[i] }} />
                              <span>{s.status}: {s.count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted text-sm">Tidak ada data.</p>
                  )}
                </ChartCard>
              </div>

              <div className="dash-chart-col-3">
                <ChartCard title="Sebaran Harga" subtitle="Distribusi per rentang harga">
                  {spStats.price_ranges?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={spStats.price_ranges} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f5" vertical={false} />
                        <XAxis dataKey="range" stroke="#adb5bd" fontSize={9} angle={-20} textAnchor="end" height={60} />
                        <YAxis stroke="#adb5bd" fontSize={11} />
                        <Tooltip />
                        <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={24} fill="#11cdef" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-muted text-sm">Tidak ada data.</p>
                  )}
                </ChartCard>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
